import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Icon, Input } from "@sagot/ui";
import { useState, type ReactNode } from "react";
import { FormAlert } from "../../../auth/components/form-alert";

interface TypedConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body: ReactNode;
  /** Label above the input, e.g. "Type DELETE to confirm". */
  confirmLabel: string;
  /** The exact text that enables the destructive button. */
  expected: string;
  submitLabel: string;
  pending: boolean;
  error?: string | null;
  onConfirm: (typed: string) => void;
}

/** Destructive confirmation: the submit button stays disabled until the exact text is typed. */
export function TypedConfirmDialog({ open, onOpenChange, title, body, confirmLabel, expected, submitLabel, pending, error, onConfirm }: TypedConfirmDialogProps) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  const matches = typed.trim() === expected;
  const change = (next: boolean) => {
    if (!next) setTyped("");
    onOpenChange(next);
  };
  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent
        title={
          <span className="flex items-center gap-2">
            <Icon name="alert-triangle" className="size-5 text-danger" />
            {title}
          </span>
        }
        closeLabel={t("common.close")}
        className="max-w-md"
        footer={
          <>
            <Button variant="ghost" onClick={() => { change(false); }}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" form="typed-confirm-form" variant="danger" disabled={!matches} loading={pending}>
              <Icon name="trash" />
              {submitLabel}
            </Button>
          </>
        }
      >
        <form
          id="typed-confirm-form"
          noValidate
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (matches) onConfirm(typed.trim());
          }}
        >
          {error ? <FormAlert title={error} /> : null}
          <div className="rounded-md bg-danger-soft p-3 text-sm text-danger">{body}</div>
          <Field label={confirmLabel}>
            <Input value={typed} onChange={(e) => { setTyped(e.target.value); }} placeholder={expected} autoComplete="off" className="font-mono" />
          </Field>
        </form>
      </DialogContent>
    </Dialog>
  );
}
